import {
  Component,
  ElementRef,
  HostListener,
  signal,
  computed,
  inject,
  effect,
  OnInit,
  ViewChild,
} from '@angular/core';
import {
  ShamLocationAddressIcon,
  ShamShamLogoFullIcon,
  ShamHeartIcon,
  ShamCartIcon,
  ShamBellIcon,
  ShamFavourite01Icon,
  ShamArrowDown01SolidIcon,
  ShamArrowDown01StrokeIcon,
  ShamSettingsIcon,
  ShamLogout01BulkIcon,
  ShamMoreVerticalIcon,
  ShamBag01Icon,
  ShamNotification01Icon,
} from 'sham-components/icons';
import { RouterLinkActive, RouterLinkWithHref, Router } from '@angular/router';
import { WebsiteContext } from '@/app/services/website-context';
import { AuthService } from '@/app/services/auth.service';
import { TranslateModule } from '@ngx-translate/core';
import { ShamUsaFlagIcon, ShamSyFlagIcon } from 'sham-components/icons';
import { LanguageSwitcherComponent } from 'sham-components/shared/language-switcher';
import { CartApi } from '@/app/services/cart-api';
import { SELLER_DASH_URL } from '@/environments/environment';
import {
  CustomerNotificationItem,
  CustomerNotificationsStore,
} from '@/app/services/customer-notifications.store';
import { AccountApi, AccountSummary } from '@/app/services/account.api';
import { CommonModule } from '@angular/common';
import { MarketplaceSearch } from './marketplace-search/marketplace-search';

export interface SubCategory {
  name: string;
  slug: string;
}

export interface SubCategoryGroup {
  title: string;
  link?: string;
  items: SubCategory[];
}

export interface Category {
  id: string;
  name: string;
  slug: string;
  subGroups: SubCategoryGroup[];
}

@Component({
  selector: 'app-main-header',
  imports: [
    ShamLocationAddressIcon,
    ShamShamLogoFullIcon,
    ShamHeartIcon,
    ShamCartIcon,
    RouterLinkActive,
    RouterLinkWithHref,
    TranslateModule,
    ShamUsaFlagIcon,
    ShamSyFlagIcon,
    LanguageSwitcherComponent,
    ShamBellIcon,
    CommonModule,
    ShamArrowDown01StrokeIcon,
    ShamSettingsIcon,
    ShamLogout01BulkIcon,
    ShamMoreVerticalIcon,
    MarketplaceSearch,
    ShamFavourite01Icon,
    ShamBag01Icon,
    ShamNotification01Icon,
  ],
  templateUrl: './main-header.html',
  styleUrl: './main-header.css',
  host: {
    class: 'contents',
  },
})
export class MainHeader implements OnInit {
  @ViewChild('accountMenuWrapper', { read: ElementRef })
  private readonly accountMenuWrapper?: ElementRef<HTMLElement>;

  isMegaMenuOpen = signal(false);
  isNotificationsOpen = signal(false);
  isNotificationsRendered = signal(false);
  notificationActionsOpenFor = signal<string | null>(null);
  accountMenuOpen = signal(false);
  activeCategoryIndex = signal(0);
  router = inject(Router);
  ctx = inject(WebsiteContext);
  auth = inject(AuthService);
  cartApi = inject(CartApi);
  notificationsStore = inject(CustomerNotificationsStore);
  accountApi = inject(AccountApi);

  account = signal<AccountSummary | null>(null);
  sellerDashboardUrl = SELLER_DASH_URL;
  cart = this.cartApi.cartSnapshot;
  // أضف هذه الـ computed داخل كلاس MainHeader
  user = computed(() => {
    if (this.auth.isLoggedIn()) {
      return this.auth.getUser();
    }
    return null;
  });

  readonly accountName = computed(() => {
    const userName =
      this.account()?.userName ?? this.user()?.userName ?? this.user()?.email ?? null;

    return userName?.split('@')[0] || null;
  });

  readonly profilePhotoUrl = computed(() => {
    return this.account()?.profilePhotoUrl ?? this.user()?.profilePhotoUrl ?? null;
  });

  logout() {
    this.closeAccountMenu();
    this.auth.logout();
    this.router.navigate(['/home']);
  }

  ngOnInit(): void {
    if (this.auth.isLoggedIn()) {
      this.loadAccountSummary();
    }
  }

  constructor() {
    effect(() => {
      console.log('LANG CHANGED:', this.ctx.lang());
    });
    effect(() => {
      if (this.auth.isLoggedIn()) {
        this.loadAccountSummary();
      } else {
        this.account.set(null);
      }
    });
    void this.ctx.loadCategoriesTree(this.ctx.lang());
    this.loadCart();
  }

  loadCart() {
    this.cartApi.getCart().subscribe({
      next: () => {},
      error: (err) => console.error(err),
    });
  }

  cartItemsCount = computed(() => {
    const cart = this.cart();

    if (!cart) return 0;

    return cart.sellerGroups.reduce((total, group) => total + group.items.length, 0);
  });

  cartTotal = computed(() => {
    return this.cart()?.total ?? 0;
  });

  readonly notificationPreview = this.notificationsStore.previewItems;
  readonly unreadNotificationsCount = this.notificationsStore.unreadCount;
  readonly isNotificationsLoading = this.notificationsStore.isActiveLoading;

  categoriesData = computed<Category[]>(() => {
    const tree = this.ctx.categoriesTree();
    return tree.map((level0) => ({
      id: level0.id,
      name: level0.name,
      slug: level0.slug,
      subGroups: (level0.children || []).map((level1) => ({
        title: level1.name,
        link: level1.slug,
        items: (level1.children || []).map((level2) => ({
          name: level2.name,
          slug: level2.slug,
        })),
      })),
    }));
  });

  activeCategory = computed(() => this.categoriesData()[this.activeCategoryIndex()]);

  handleAllCategoriesClick(event: Event) {
    event.stopPropagation();
    event.preventDefault();
    const isLargeScreen = globalThis.matchMedia('(min-width: 1024px)').matches;

    if (isLargeScreen) {
      this.toggleMegaMenu(event);
    } else {
      this.isMegaMenuOpen.set(false);
      this.router.navigate(['/categories']);
    }
  }

  toggleMegaMenu(event: Event) {
    event.stopPropagation();
    this.isMegaMenuOpen.update((v) => !v);
  }

  toggleNotifications(event: Event) {
    event.stopPropagation();
    this.closeAccountMenu();

    if (this.isNotificationsOpen()) {
      this.closeNotifications();
      return;
    }

    this.isNotificationsRendered.set(true);
    this.notificationsStore.ensureReady();
    requestAnimationFrame(() => {
      this.isNotificationsOpen.set(true);
    });
  }

  markAllNotificationsAsRead(event?: Event) {
    event?.stopPropagation();
    this.notificationsStore.markAllActiveAsRead();
  }

  goToNotifications(event?: Event) {
    event?.stopPropagation();
    this.closeNotifications();
    void this.router.navigate(['/profile/notifications']);
  }

  openNotification(notification: CustomerNotificationItem, event?: Event) {
    event?.stopPropagation();
    this.closeNotifications();
    this.notificationsStore.openNotification(notification);
  }

  toggleNotificationActions(notificationId: string, event: Event) {
    event.stopPropagation();
    this.notificationActionsOpenFor.update((openFor) =>
      openFor === notificationId ? null : notificationId,
    );
  }

  runNotificationAction(
    action: 'markUnread' | 'archive' | 'delete',
    notificationId: string,
    event: Event,
  ) {
    event.stopPropagation();
    this.notificationActionsOpenFor.set(null);

    if (action === 'markUnread') {
      this.notificationsStore.markAsUnread(notificationId);
      return;
    }

    if (action === 'archive') {
      this.notificationsStore.archive(notificationId);
      return;
    }

    this.notificationsStore.delete(notificationId);
  }

  notificationsLoadingLabel() {
    return this.ctx.lang() === 'ar'
      ? 'جاري تحميل الإشعارات...'
      : 'Loading notifications...';
  }

  notificationsEmptyLabel() {
    return this.ctx.lang() === 'ar'
      ? 'لا توجد إشعارات حتى الآن.'
      : 'No notifications yet.';
  }

  closeNotifications() {
    if (!this.isNotificationsRendered()) {
      return;
    }

    this.isNotificationsOpen.set(false);
    this.notificationActionsOpenFor.set(null);

    setTimeout(() => {
      if (!this.isNotificationsOpen()) {
        this.isNotificationsRendered.set(false);
      }
    }, 220);
  }

  toggleAccountMenu(event: Event) {
    event.stopPropagation();
    this.closeNotifications();
    this.accountMenuOpen.update((open) => !open);
  }

  closeAccountMenu() {
    this.accountMenuOpen.set(false);
  }

  onAccountMenuAction() {
    this.closeAccountMenu();
  }

  setActiveCategory(index: number) {
    this.activeCategoryIndex.set(index);
  }

  private loadAccountSummary() {
    this.accountApi.getMe().subscribe({
      next: (res) => this.account.set(res),
      error: (err: any) => console.log(err),
    });
  }

  @HostListener('document:click', ['$event'])
  closeOverlays(event: MouseEvent) {
    this.isMegaMenuOpen.set(false);
    this.closeNotifications();
    this.notificationActionsOpenFor.set(null);

    if (!this.accountMenuOpen()) {
      return;
    }

    const target = event.target as Node | null;
    const wrapper = this.accountMenuWrapper?.nativeElement;
    if (target && wrapper && !wrapper.contains(target)) {
      this.closeAccountMenu();
    }
  }

  @HostListener('document:keydown.escape')
  onEscapeKey() {
    this.closeNotifications();
    this.closeAccountMenu();
  }

  getInitials(name: string | null): string {
    if (!name) return '';

    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((word) => word[0].toUpperCase())
      .join('');
  }
}
